import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import './GraphicTryoutMinimal.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicTryoutMinimal({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam,
    ageGroup,
    tryoutDate,
    tryoutTime,
    location,
    contact,
    additionalInfo,
  } = graphicData;

  const accent = accentBlockColor || teamColors.primary;
  const detailColor = detailTextColor || null;

  const getBackgroundStyle = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 0%, ${teamColors.primary} 50%, ${teamColors.secondary} 50%, ${teamColors.secondary} 100%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const isLightColor = (color) => {
    if (!color || color === 'null') return false; // Default to dark if color is null/undefined
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  const getTextColor = () => {
    const primaryLight = isLightColor(teamColors.primary);
    const secondaryLight = isLightColor(teamColors.secondary);

    if (primaryLight && secondaryLight) {
      return '#2d3748';
    }
    if (primaryLight) {
      return '#374151';
    }
    return '#ffffff';
  };

  const getTextShadow = () => {
    const textColor = getTextColor();
    if (textColor !== '#ffffff') {
      return '1px 1px 2px rgba(255, 255, 255, 0.3)';
    }
    return '3px 3px 6px rgba(0, 0, 0, 0.3)';
  };

  const textColor = getTextColor();
  const textShadow = getTextShadow();

  return (
    <div
      className="graphic-tryout-minimal"
      id="graphic-tryout"
      style={{
        background: getBackgroundStyle(),
        color: textColor,
      }}
    >
      {/* Top right logo */}
      <div className="tryout-minimal-top-logo">
        {homeTeamLogo ? (
          <div className="team-logo-circle team-logo-circle-md">
            <img
              src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
              alt={homeTeam}
              className="tryout-minimal-top-logo-img"
            />
          </div>
        ) : (
          <div className="team-logo-circle team-logo-circle-md">
            <div className="tryout-minimal-top-logo-placeholder">
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="tryout-minimal-content">
        <div
          className="tryout-minimal-badge"
          style={{
            background: accent,
            fontFamily: heroFont,
            fontSize: heroFontSize * 0.11 + 'px',
          }}
        >
          TRYOUTS
        </div>

        <div className="tryout-minimal-team">
          <div className="tryout-minimal-team-name" style={{ textShadow }}>
            {homeTeam || 'TEAM NAME'}
          </div>
          {ageGroup && (
            <div className="tryout-minimal-age-group">{ageGroup}</div>
          )}
        </div>

        <div
          className="tryout-minimal-details-box"
          style={{
            background:
              textColor === '#ffffff'
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.08)',
            border:
              textColor === '#ffffff'
                ? '1px solid rgba(255, 255, 255, 0.15)'
                : '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {tryoutDate && (
            <div className="tryout-minimal-detail-line">
              <span className="tryout-minimal-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>DATE</span>
              <span style={detailColor ? { color: detailColor } : {}}>{formatGameDate(tryoutDate, dateFormat)}</span>
            </div>
          )}
          {tryoutTime && (
            <div className="tryout-minimal-detail-line">
              <span className="tryout-minimal-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>TIME</span>
              <span style={detailColor ? { color: detailColor } : {}}>{formatGameTime(tryoutTime)}</span>
            </div>
          )}
          {location && (
            <div className="tryout-minimal-detail-line">
              <span className="tryout-minimal-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>LOCATION</span>
              <span style={detailColor ? { color: detailColor } : {}}>{location}</span>
            </div>
          )}
          {contact && (
            <div className="tryout-minimal-detail-line">
              <span className="tryout-minimal-label" style={detailColor ? { color: detailColor, opacity: 0.7 } : { opacity: 0.7 }}>CONTACT</span>
              <span style={detailColor ? { color: detailColor } : {}}>{contact}</span>
            </div>
          )}
        </div>

        {additionalInfo && (
          <div
            className="tryout-minimal-additional"
            style={{
              background:
                textColor === '#ffffff'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.05)',
            }}
          >
            {additionalInfo}
          </div>
        )}
      </div>

      {showWatermark && (
        <div className="playmaker-watermark">
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" />
        </div>
      )}
    </div>
  );
}

export default GraphicTryoutMinimal;
